import { useState } from "react";
import { generatePath, useLocation } from "react-router";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { CalendarDays } from "@acto/web-common/components";
import type Customer from "@acto/taskpane/core/domain/Customer";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { AnalyticsEvents, AnalyticsSourceScope, useTrack, useViewTrackedOnce } from "@acto/taskpane/core/analytics";
import { CustomerSection } from "@acto/taskpane/field-sales-2/components/patterns/CustomerSection";
import { MeetingCalendar } from "@acto/taskpane/field-sales-2/components/patterns/MeetingCalendar";
import { useNearbyCustomersFlow } from "@acto/taskpane/field-sales-2/components/patterns/NearbyCustomers";
import { usePlanMeetingFlow } from "@acto/taskpane/field-sales-2/components/patterns/PlanMeetingSheet";
import { BottomNavActionPill } from "@acto/taskpane/field-sales-2/components/patterns/BottomNav/BottomNavActionPill";
import { useHomeNavActionPill } from "@acto/taskpane/field-sales-2/HomeNavLayout";
import { useDirectionalNavigate } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import { ScrollPage, ScrollPageTitle } from "@acto/taskpane/field-sales-2/components/primitives/ScrollPage";
import { useContributeChatScope, customersToClients } from "@acto/taskpane/core/chat-envelope";
import { CLIENT_LIST_LIMIT, INCLUDE_CLIENT_DETAILS } from "@acto/taskpane/field-sales-2/utils/chatScope";
import { PeriodComparisonScope } from "@acto/taskpane/field-sales-2/components/patterns/PeriodComparisonScope/PeriodComparisonScope";
import { YEAR_TO_DATE_SELECTION } from "@acto/taskpane/field-sales-2/utils/periodSelection";
import { useFocusCustomerList } from "./useFocusCustomerList";

const FOCUS_PAGE_SIZE = 3;

export function Calendar() {
  const t = useTranslations("fieldSalesTwo");
  const { locale } = useLocale();
  const tNav = useTranslations("fieldSalesTwo.bottomNav");
  const navigate = useDirectionalNavigate();
  const location = useLocation();
  const track = useTrack();

  // "Kunden im Fokus" = only customers that have an observation, paginated (3, then more on scroll).
  const { customers, revenuePeriod, isLoading, isLoadingMore, hasMore, loadMore } = useFocusCustomerList({
    pageSize: FOCUS_PAGE_SIZE,
  });

  const { openPicker, openPlan, openPlanOnDay, openPickerOnDay, sheets } = usePlanMeetingFlow();
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const nearby = useNearbyCustomersFlow({ selectedDay, openPlanOnDay, openPickerOnDay });

  useViewTrackedOnce(() => track(AnalyticsEvents.CALENDAR_VIEWED, {}));

  // Ambient context: contribute the rendered focus client list to the calendar scope.
  const clientsFragment = customersToClients(customers, {
    includeDetails: INCLUDE_CLIENT_DETAILS,
    locale,
    revenuePeriod,
    limit: CLIENT_LIST_LIMIT,
  });
  useContributeChatScope("calendar", clientsFragment);

  useHomeNavActionPill(
    <BottomNavActionPill onClick={() => openPicker()}>
      <CalendarDays aria-hidden="true" />
      {tNav("addMeeting")}
    </BottomNavActionPill>,
  );

  const handleOpenCustomer = (customer: Customer) => {
    navigate(generatePath(ActoRoutes.CUSTOMER_DETAILS, { id: encodeURIComponent(customer.id) }), {
      state: { from: location.pathname },
    });
  };

  return (
    <AnalyticsSourceScope source="calendar">
      <PeriodComparisonScope selection={YEAR_TO_DATE_SELECTION}>
        <ScrollPage>
          <MeetingCalendar
            showViewToggle
            onSelectedDateChange={setSelectedDay}
            onDayMeetingsChange={nearby.onDayMeetingsChange}
            dayFooter={nearby.entry}
            contributeTo="calendar"
          />
          <ScrollPageTitle>{t("home.focusTitle")}</ScrollPageTitle>
          <CustomerSection
            customers={customers}
            revenuePeriod={revenuePeriod}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            loadMore={loadMore}
            skeletonCount={FOCUS_PAGE_SIZE}
            onOpenCustomer={handleOpenCustomer}
            primaryAction={(customer) => ({
              label: t("customerCard.addToDay"),
              onClick: () => openPlan(customer.id, selectedDay),
            })}
          />
          {nearby.sheet}
          {sheets}
        </ScrollPage>
      </PeriodComparisonScope>
    </AnalyticsSourceScope>
  );
}
